import { Link, NavLink } from 'react-router-dom';
import { FintMark } from '@/features/common/ui';
import { cn } from '@/lib/cn';
import { PUBLISHER } from './legal-facts';

/**
 * The frame of Fint's public pages - the privacy policy, the terms and the
 * page that explains connecting an assistant.
 *
 * ── WHY THESE ARE PUBLIC ROUTES IN THE SPA ──
 * The two directories that list connectors (Anthropic's and OpenAI's) ask for
 * a privacy policy URL and a documentation URL, and they fetch them signed
 * out. So the routes sit outside both `ProtectedRoute` and `GuestRoute`: a
 * signed-in person who follows a footer link must not be bounced to the
 * dashboard, and a signed-out reviewer must not be bounced to the login.
 * The server serves the SPA shell for any non-API path, so they need nothing
 * on the API side.
 */
export function LegalShell({ title, updated, children }: { title: string; updated?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 px-4 py-3">
          <Link to="/" className="flex touch:min-h-11 items-center gap-2 text-lg font-semibold tracking-tight text-primary">
            <FintMark className="size-7" />
            Fint
          </Link>
          <LegalNav />
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {updated ? <p className="mt-1 text-sm text-muted-foreground">Effective {updated}</p> : null}
        <div className="mt-6 space-y-8 text-[15px] leading-relaxed">{children}</div>
      </main>
      <footer className="border-t">
        <div className="mx-auto max-w-3xl space-y-1 px-4 py-6 text-sm text-muted-foreground">
          <p>
            Fint is published by {PUBLISHER.name}, {PUBLISHER.address.join(', ')}.
          </p>
          <p>
            Questions: <Mail />
          </p>
        </div>
      </footer>
    </div>
  );
}

const LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/docs/mcp', label: 'Connect an assistant' },
] as const;

export function LegalNav({ className }: { className?: string }) {
  return (
    <nav aria-label="About Fint" className={cn('flex flex-wrap gap-x-4 gap-y-1 text-sm', className)}>
      {LINKS.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          className={({ isActive }) =>
            cn('inline-flex touch:min-h-11 touch:min-w-11 items-center justify-center hover:text-foreground', isActive ? 'font-medium text-foreground' : 'text-muted-foreground')
          }
        >
          {l.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function Section({ id, title, children }: { id?: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

export function List({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc space-y-1.5 pl-5">{children}</ul>;
}

export function Mail() {
  return (
    <a className="text-primary hover:underline" href={`mailto:${PUBLISHER.email}`} data-tap-exempt="text link in a sentence">
      {PUBLISHER.email}
    </a>
  );
}
